import { useEffect, useId, useMemo, useRef, useState } from 'react'

import styles from './SearchableSelect.module.scss'

export type SearchableOption = { value: string; label: string }

const MAX_VISIBLE_OPTIONS = 50

const stripAccents = (text: string) => text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

/** Drops punctuation so "12345678" matches "12.345.678-5". */
const compact = (text: string) => stripAccents(text).replace(/[^a-z0-9]/g, '')

function getMatches(options: readonly SearchableOption[], query: string): readonly SearchableOption[] {
  const trimmed = query.trim()
  if (trimmed === '') {
    return options
  }
  const loose = stripAccents(trimmed)
  const tight = compact(trimmed)
  return options.filter(
    (option) => stripAccents(option.label).includes(loose) || (tight !== '' && compact(option.label).includes(tight)),
  )
}

export function SearchableSelect({
  id,
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  onDisabledClick,
  required = false,
  pinnedOption,
}: {
  id: string
  value: string
  options: readonly SearchableOption[]
  onChange: (value: string) => void
  placeholder: string
  disabled?: boolean
  onDisabledClick?: () => void
  required?: boolean
  /** Always offered, whatever the query matched or the cap cut — "Crear nuevo paciente". */
  pinnedOption?: SearchableOption
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listId = `${useId()}-listbox`

  const selected = [...options, ...(pinnedOption ? [pinnedOption] : [])].find((option) => option.value === value) ?? null
  const allMatches = useMemo(() => getMatches(options, open ? query : ''), [options, query, open])
  const visible = allMatches.slice(0, MAX_VISIBLE_OPTIONS)
  const hiddenCount = allMatches.length - visible.length
  const matches = pinnedOption ? [...visible, pinnedOption] : visible

  const close = () => {
    setOpen(false)
    setQuery('')
  }

  useEffect(() => {
    if (!open) {
      return
    }
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [open])

  // Arrowing past the visible rows has to bring them into view or the highlight
  // is invisible for every option below the fold.
  useEffect(() => {
    if (!open) {
      return
    }
    const row = listRef.current?.querySelectorAll('[role="option"]')[activeIndex]
    row?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open])

  const commit = (option: SearchableOption) => {
    onChange(option.value)
    close()
    inputRef.current?.focus()
  }

  const openList = () => {
    if (disabled) {
      onDisabledClick?.()
      return
    }
    const selectedIndex = options.slice(0, MAX_VISIBLE_OPTIONS).findIndex((option) => option.value === value)
    setOpen(true)
    setQuery('')
    setActiveIndex(selectedIndex > -1 ? selectedIndex : 0)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        openList()
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((current) => (matches.length === 0 ? 0 : (current + step + matches.length) % matches.length))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const option = matches[activeIndex]
      if (open && option) {
        commit(option)
      } else if (!open) {
        openList()
      }
      return
    }
    if (event.key === 'Escape' && open) {
      // Keeps the Escape from also reaching a surrounding Modal, which closes on it.
      event.stopPropagation()
      close()
    }
  }

  return (
    <div className={styles.root} ref={containerRef}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        className={`form-control ${styles.input}${disabled ? ` ${styles.isDisabled}` : ''}`}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-required={required}
        aria-disabled={disabled}
        aria-activedescendant={open && matches[activeIndex] ? `${listId}-${activeIndex}` : undefined}
        autoComplete="off"
        readOnly={disabled}
        placeholder={placeholder}
        value={open ? query : (selected?.label ?? '')}
        onMouseDown={(event) => {
          if (disabled) {
            event.preventDefault()
            onDisabledClick?.()
          }
        }}
        onFocus={() => !disabled && setOpen(true)}
        onClick={openList}
        // Options commit on mousedown with preventDefault, so this only fires for
        // a real exit — tabbing away must not leave the list open behind.
        onBlur={close}
        onChange={(event) => {
          setQuery(event.target.value)
          setActiveIndex(0)
          setOpen(true)
        }}
        onKeyDown={handleKeyDown}
      />
      <i className={`fa fa-caret-down ${styles.caret}`} aria-hidden="true" />
      {open && (
        <ul className={styles.list} id={listId} role="listbox" ref={listRef}>
          {visible.length === 0 && <li className={styles.empty}>Sin resultados</li>}
          {visible.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              data-testid="searchable-select-option"
              className={`${styles.option}${index === activeIndex ? ` ${styles.isActive}` : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault()
                commit(option)
              }}
            >
              {option.label}
            </li>
          ))}
          {hiddenCount > 0 && <li className={styles.empty}>{hiddenCount} más — sigue escribiendo para filtrar</li>}
          {pinnedOption && (
            <li
              key={pinnedOption.value}
              id={`${listId}-${visible.length}`}
              role="option"
              aria-selected={pinnedOption.value === value}
              data-testid="searchable-select-option"
              className={`${styles.option} ${styles.pinned}${visible.length === activeIndex ? ` ${styles.isActive}` : ''}`}
              onMouseEnter={() => setActiveIndex(visible.length)}
              onMouseDown={(event) => {
                event.preventDefault()
                commit(pinnedOption)
              }}
            >
              <i className="fa fa-plus" aria-hidden="true" /> {pinnedOption.label}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
